import { memo, useCallback, useMemo, useState } from 'react';

import { Button } from '@air/primitive-button';
import { useToasts } from '@air/provider-toast';

import { useAddMemberToLibraries } from '~/components/LibraryBeta/hooks/mutations/useAddMemberToLibraries';
import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { useGeneralLibraryMetadata } from '~/components/LibraryBeta/hooks/useGeneralLibraryMetadata';
import { InviteToLibrariesStepLibraryRow } from '~/components/Modals/InviteMembers/InviteMembersModal/InviteToLibrariesStepLibraryRow';
import { useWorkspaceRolesContext } from '~/providers/WorkspaceRolesProvider';

export type InviteToLibrariesStepProps = {
  email: string;
  onComplete: () => void;
};

export const InviteToLibrariesStep = memo(({ email, onComplete }: InviteToLibrariesStepProps) => {
  // @TODO(JAN-976): Add load-more support for workspaces with >100 libraries
  const { data: librariesData } = useLibraries();
  const {
    addMemberToLibraries: { mutate: sendLibraryInvites, isPending: isSendingInvites },
  } = useAddMemberToLibraries();
  const { showToast } = useToasts();
  const { generalLibraryTitle, generalLibraryIcon, generalLibraryColor } = useGeneralLibraryMetadata();
  const { data: roles } = useWorkspaceRolesContext();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const libraries = useMemo(() => librariesData?.pages.flatMap((page) => page.data) ?? [], [librariesData]);

  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  const handleSendInvites = useCallback(() => {
    const selected = libraries
      .filter((lib) => selectedIds.has(lib.id))
      .map((lib) => ({ libraryId: lib.id, roleId: lib.defaultRoleId }));

    sendLibraryInvites(
      { email, libraries: selected },
      {
        onSuccess: ({ succeeded, total }) => {
          const message =
            succeeded.length === total
              ? `Invited to ${total} ${total === 1 ? 'library' : 'libraries'}`
              : `Invited to ${succeeded.length} of ${total} libraries`;
          showToast(message);
          onComplete();
        },
        onError: () => {
          showToast('Failed to add to libraries. Please try again.');
        },
      },
    );
  }, [sendLibraryInvites, email, libraries, onComplete, selectedIds, showToast]);

  return (
    <div className="mt-6">
      <p className="mb-4 text-14 text-grey-9">
        This user ({email}) has been invited to your workspace and the global library. You can also add them to
        additional libraries.
      </p>
      <ul className="mb-6 flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-8 border border-grey-5 p-3">
        <li>
          <InviteToLibrariesStepLibraryRow
            id="general"
            title={generalLibraryTitle}
            colorHex={generalLibraryColor.hex}
            icon={generalLibraryIcon}
            isSelected
            disabled
            onToggle={handleToggle}
          />
        </li>
        {libraries.map((library) => (
          <li key={library.id}>
            <InviteToLibrariesStepLibraryRow
              id={library.id}
              title={library.title}
              colorHex={library.color.backgroundHex}
              icon={library.icon}
              isSelected={selectedIds.has(library.id)}
              roleName={roles?.find((r) => r.id === library.defaultRoleId)?.displayName}
              onToggle={handleToggle}
            />
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-2">
        <Button appearance="ghost" size="large" onClick={onComplete}>
          Skip
        </Button>
        <Button
          appearance="filled"
          disabled={selectedIds.size === 0 || isSendingInvites}
          isLoading={isSendingInvites}
          onClick={handleSendInvites}
          size="large"
        >
          Invite
        </Button>
      </div>
    </div>
  );
});

InviteToLibrariesStep.displayName = 'InviteToLibrariesStep';
